import API_ROUTES from "../constants/api";
import { ReportPageType, ReportSection } from "../constants/common";
import { getTranslation, normalizeLanguageCode } from "./TranslationHelpers";
import { buildModernReportCoverHtml, UAE_FDL_REPORT_FONT_FAMILY } from "./reportCoverMetadataHtml";
import { buildUaeFdlTableOfContentsHtml, escapeTocHtml } from "./reportTocWordHtml";
import Utils from "./Utils";

const allowedTypes = [
  'SINGLE_VALUE', 'TYPE_1_TABLE', 'TYPE_2_TABLE', 'TYPE_3_TABLE',
  'PAGE', 'TYPE_5_TABLE', 'TYPE_6_TABLE', 'TYPE_7_TABLE',
];

const hasTemplateData = (data) =>
  data.length > 0 && data.some(
    (item) => allowedTypes.includes(item.keyValueType)
      && (item.keyValueType !== 'SINGLE_VALUE' || (item.keyValues.length > 0 && !!item.keyValues[0].value)),
  );

const TEMPLATE_BLOCK_SPACING = 'margin-bottom: 20px;';

const wrapTemplateBlock = (html) => (html ? `<div style="${TEMPLATE_BLOCK_SPACING}">${html}</div>` : '');

const GHG_DECIMAL_PLACES_MAX = 15;
const UAE_FDL_MIN_TABLE_DECIMAL_PLACES = 4;

/**
 * Normalizes company decimal-places setting for GHG tables.
 * @param {unknown} raw
 * @returns {number}
 */
const normalizeGhgDecimalPlaces = (raw) => {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return 2;
  return Math.min(Math.floor(n), GHG_DECIMAL_PLACES_MAX);
};

/**
 * When company report settings include `decimalPlaces`, returns the normalized value.
 * Otherwise returns null so callers can keep legacy formatting (e.g. VSME / older reports).
 * @param {{ decimalPlaces?: number|string|null }} [settings]
 * @returns {number|null}
 */
const resolveGhgCompanyDecimalPlaces = (settings) => {
  if (!settings || settings.decimalPlaces === undefined || settings.decimalPlaces === null || settings.decimalPlaces === '') {
    return null;
  }
  return normalizeGhgDecimalPlaces(settings.decimalPlaces);
};

/**
 * UAE FDL tables use at least 4 decimal places so small emission values are not rounded to zero.
 * @param {{ decimalPlaces?: number|string|null }} [settings]
 * @returns {number}
 */
const resolveUaeFdlTableDecimalPlaces = (settings) => {
  const companyDp = resolveGhgCompanyDecimalPlaces(settings);
  if (companyDp == null) {
    return UAE_FDL_MIN_TABLE_DECIMAL_PLACES;
  }
  return Math.max(companyDp, UAE_FDL_MIN_TABLE_DECIMAL_PLACES);
};

/**
 * Formats a template answer for GHG tables using company decimal places and optional UOM.
 * @param {{ value?: unknown, uom?: string }} answer
 * @param {number} decimalPlaces
 */
const formatGhgTableNumericAnswer = (answer, decimalPlaces) => {
  if (!answer) return '';
  if (answer.uom === 'no.') {
    const v = answer.value;
    if (v === null || v === undefined || v === '') return '';
    return String(parseInt(v, 10));
  }
  const rounded = Utils.roundToDecimalPlacesIfNumber(answer.value, decimalPlaces);
  if (rounded === '') return '';
  if (answer.uom) return `${rounded} ${answer.uom}`;
  return rounded;
};

const qAnserData = (answer) => {
  if (!answer) return '';

  const formatValue = (value) => {
    // Convert to string to preserve decimal precision
    const valueStr = String(value);

    // Check if the value contains a decimal point
    if (valueStr.includes('.')) {
      // Split into integer and decimal parts
      const parts = valueStr.split('.');
      const integerPart = parts[0];
      const decimalPart = parts[1];

      // Check if all decimal digits are zeros
      if (decimalPart && /^0+$/.test(decimalPart)) {
        // All decimals are zeros, return just the integer part
        return integerPart;
      }

      // Has meaningful decimals, return as-is (preserving precision)
      return valueStr;
    }

    // No decimal point, return as-is
    return valueStr;
  };

  if (!!answer.uom) {
    if (answer.uom == "no.") {
      return parseInt(answer.value);
    } else {
      const formattedValue = formatValue(answer.value);
      return formattedValue + ' ' + answer.uom;
    }
  }
  return formatValue(answer.value);
}

/**
 * Matches template language code with report language code
 * Handles case-insensitive comparison and matches "EN" with "en-uk", "en-us", etc.
 * @param {string} templateLangCode - Language code from template data (e.g., "EN", "DE", "AR")
 * @param {string} reportLangCode - Language code from report (e.g., "en-uk", "en-us", "de")
 * @returns {boolean} True if codes match
 */
const matchesLanguageCode = (templateLangCode, reportLangCode) => {
  if (!templateLangCode || !reportLangCode) return false;

  // Normalize report language code (e.g., "en-uk" -> "en", "de" -> "de")
  const normalizedReportLang = normalizeLanguageCode(reportLangCode);

  // Extract base language from template code (e.g., "EN" -> "en", "DE" -> "de")
  // Handle both "EN" and "EN-UK" formats
  const templateLangLower = templateLangCode.toLowerCase();
  const baseTemplateLang = templateLangLower.split('-')[0];

  // Compare normalized codes (case-insensitive)
  return baseTemplateLang === normalizedReportLang;
}

const pageHeaderFunction = (templatedata, lang, type) => {
  let findLangValue = templatedata.localization.find(data => matchesLanguageCode(data.languageCode, lang))
  if (!findLangValue) {
    findLangValue = templatedata.localization.find(data => matchesLanguageCode(data.languageCode, 'en'))
  }
  if (!!findLangValue) {
    if (type == 'Social') {
      return (`<h4 style="margin:0;padding:0;margin-left:0;padding-left:0;"><span style="font-size: 24px; color: #007bff;"><strong>${findLangValue.text}</strong></h4>
      <div style="font-family: ${UAE_FDL_REPORT_FONT_FAMILY};line-height: 1.6;margin:0;padding:0;margin-left:0;padding-left:0;">`)
    } else if (type == 'Governance') {
      return (`<h4 style="margin:0;padding:0;margin-left:0;padding-left:0;"><span style="font-size: 24px; color: #f7c300;"><strong>${findLangValue.text}</strong></h4>
      <div style="font-family: ${UAE_FDL_REPORT_FONT_FAMILY};line-height: 1.6;margin:0;padding:0;margin-left:0;padding-left:0;">`)
    } else {
      return (`<h4 style="margin:0;padding:0;margin-left:0;padding-left:0;"><span style="font-size: 24px; color: rgb(45, 194, 107);"><strong>${findLangValue.text}</strong></h4>
      <div style="font-family: ${UAE_FDL_REPORT_FONT_FAMILY};line-height: 1.6;margin:0;padding:0;margin-left:0;padding-left:0;">`)
    }
  } else {
    return ''
  }
}

const pageSectionFunction = (templatedata, lang) => {
  let findLangValue = templatedata.localization.find(data => matchesLanguageCode(data.languageCode, lang))
  if (!findLangValue) {
    findLangValue = templatedata.localization.find(data => matchesLanguageCode(data.languageCode, 'en'))
  }
  if (!!findLangValue) {
    return wrapTemplateBlock(`<h5 style='font-size:24px;margin:0;font-weight: 700;'>${findLangValue.text}</h5>`)
  } else {
    return ''
  }
}

const pageSubSectionFunction = (templatedata, lang, type) => {
  let findLangValue = templatedata.localization.find(data => matchesLanguageCode(data.languageCode, lang))
  if (!findLangValue) {
    findLangValue = templatedata.localization.find(data => matchesLanguageCode(data.languageCode, 'en'))
  }
  if (!!findLangValue) {
    return wrapTemplateBlock(`<h6 style='font-size: 20px;margin:0;font-weight: 600;'>${findLangValue.text}</h6>`)
  } else {
    return ''
  }
}

const pageQuestionFunction = (templatedata, lang) => {
  let findLangValue = templatedata.localization.find(val => matchesLanguageCode(val.languageCode, lang))
  if (!findLangValue) {
    findLangValue = templatedata.localization.find(val => matchesLanguageCode(val.languageCode, 'en'))
  }
  if (!!findLangValue) {
    return wrapTemplateBlock(
      `<div style="margin-bottom: 12px;font-size:14px;"><span style="color: rgb(52, 73, 94);"><strong>${findLangValue.text}</strong></span></div>
    <div style="font-size:14px;text-align: justify;" class='${templatedata.key}'><span style="color: rgb(0, 0, 0);">${!!templatedata.keyValues && templatedata.keyValues.length > 0 ? qAnserData(templatedata.keyValues[0]) : ""}</span></div>`,
    )
  } else {
    return ""
  }
}

const pagetableTYPE_1Function = (templatedata, lang) => {
  var contentHtml = ''
  let findLangValue = templatedata.localization.find(val => matchesLanguageCode(val.languageCode, lang))
  if (!findLangValue) {
    findLangValue = templatedata.localization.find(val => matchesLanguageCode(val.languageCode, 'en'))
  }
  if (!!findLangValue) {
    contentHtml += `<div style="margin-bottom: 10px;font-size:14px;margin-left: 0;"><span style="color: rgb(52, 73, 94);"><strong>${findLangValue.text}</strong></span></div>`
  }
  contentHtml += `<div style="margin:0;padding:0;border: 1px solid #ddd;     border-radius: 6px;     overflow: hidden;">
    <table style='width:100%;font-size:14px;border-collapse: collapse; border-spacing: 0; border: 1px solid #fff; border-radius: 6px; overflow: hidden;'>
      <thead><tr style="background-color: rgb(241 247 249); height: 20px;">`
  const columnHeaders = {};
  const filterTopHeader = templatedata.metadata.filter(item => /^[A-Z]1$/.test(item.columnNumber) && item.columnType === "HEADER");
  filterTopHeader.forEach(item => {
    let findLangValue = item.localization.find(val => matchesLanguageCode(val.languageCode, lang))
    if (!findLangValue) {
      findLangValue = item.localization.find(val => matchesLanguageCode(val.languageCode, 'en'))
    }
    if (!!findLangValue) {
      contentHtml += `<th style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;width:${100 / filterTopHeader.length}%;"><span style="color: rgb(0, 0, 0);">${findLangValue.text}</span></th>`
    }
    columnHeaders[item.columnNumber.replace("1", "")] = item.columnNumber;
  })

  contentHtml += `</tr></thead><tbody>`
  const groupedRows = {};
  templatedata.keyValues.forEach((item) => {
    const col = item?.metadata?.columnNumber?.charAt(0);
    if (!!col) {
      if (!groupedRows[col]) groupedRows[col] = [];
      groupedRows[col].push(item);
    }
  });
  const maxRows = Math.max(...Object.values(groupedRows).map(arr => arr.length), 0);

  for (let i = 0; i < maxRows; i++) {
    contentHtml += `<tr style="height: 20px;">`

    Object.keys(columnHeaders).forEach(col => {
      contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px" class='${groupedRows[col]?.[i]?.key}'><span style="color: rgb(0, 0, 0);">${!!groupedRows[col]?.[i] ? qAnserData(groupedRows[col]?.[i]) : ""}</span></td>`
    });

    contentHtml += `</tr>`
  }
  contentHtml += `</tbody></table></div>`
  return wrapTemplateBlock(contentHtml)
}


const pagetableTYPE_2Function = (templatedata, lang, settings) => {
  const tableDp = resolveUaeFdlTableDecimalPlaces(settings);
  var contentHtml = ''
  let findLangValue = templatedata.localization.find(val => matchesLanguageCode(val.languageCode, lang))
  if (!findLangValue) {
    findLangValue = templatedata.localization.find(val => matchesLanguageCode(val.languageCode, 'en'))
  }
  if (!!findLangValue) {
    contentHtml += `<div style="margin-bottom: 10px;font-size:14px;margin-left: 0;"><span style="color: rgb(52, 73, 94);"><strong>${findLangValue.text}</strong></span></div>`
  }
  contentHtml += `<div style="margin:0;padding:0;border: 1px solid #ddd;     border-radius: 6px;     overflow: hidden;">
  <table style='width:100%;font-size:14px;border-collapse: collapse; border-spacing: 0; border: 1px solid #fff; border-radius: 6px; overflow: hidden;'><thead><tr style="background-color: rgb(241 247 249); height: 20px;">`
  const columnHeaders = {};
  const filterTopHeader = templatedata.metadata.filter(item => /^[A-Z]1$/.test(item.columnNumber) && item.columnType === "HEADER");
  filterTopHeader.forEach(item => {
    let findLangValue = item.localization.find(val => matchesLanguageCode(val.languageCode, lang))
    if (!findLangValue) {
      findLangValue = item.localization.find(val => matchesLanguageCode(val.languageCode, 'en'))
    }
    if (!!findLangValue) {
      contentHtml += `<th style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;width:${100 / filterTopHeader.length}%;font-weight:800;font-size:14px;"><span style="color: rgb(0, 0, 0);">${findLangValue.text}</span></th>`
    }
    columnHeaders[item.columnNumber.replace("1", "")] = item.columnNumber;
  })

  contentHtml += `</tr></thead><tbody>`
  const groupedRows = {};
  const filterBody = templatedata.metadata.filter(meta => !/^[A-Z]1$/.test(meta.columnNumber));
  filterBody.forEach((item) => {
    const col = item?.columnNumber?.charAt(0);
    if (!!col) {
      if (!groupedRows[col]) groupedRows[col] = [];
      groupedRows[col].push(item);
    }
  });
  const maxRows = Math.max(...Object.values(groupedRows).map(arr => arr.length), 0);

  // Check if any keyValue has BASE_YEAR dataPeriod
  const hasBaseYearData = templatedata.keyValues && templatedata.keyValues.some(kv => kv.dataPeriod === 'BASE_YEAR' && kv.emissionFactors && kv.emissionFactors.length > 0);

  for (let i = 0; i < maxRows; i++) {
    contentHtml += `<tr style="height: 20px;">`

    Object.keys(columnHeaders).forEach(col => {
      const element = groupedRows[col]?.[i]
      if (!!element) {
        let findLangV = element?.localization.find(val => matchesLanguageCode(val.languageCode, lang))
        if (!findLangV) {
          findLangV = element?.localization.find(val => matchesLanguageCode(val.languageCode, 'en'))
        }
        if (element.columnType == "STATIC_TEXT") {
          if (!!findLangV) {
            contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px">${findLangV.text}</td>`
          } else {
            contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px"></td>`
          }
        } else if (element.columnType == "HEADER") {
          if (!!findLangV) {
            contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px;font-weight:600">${findLangV.text}</td>`
          } else {
            contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px;font-weight:600"></td>`
          }
        } else if (element.columnType == "CODE_MAPPING") {
          const findAns = templatedata.keyValues.find(item => item.metadata.columnCode == element.columnCode)
          if (!!findAns) {
            const displayValue = formatGhgTableNumericAnswer(findAns, tableDp);
            contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right; height: 20px;font-size:14px" class='${findAns.key}'><span style="color: rgb(0, 0, 0);">${displayValue}</span></td>`
          } else {
            contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right; height: 20px;font-size:14px"><span style="color: rgb(0, 0, 0);">-</span></td>`
          }
        } else if (element.columnType == "BLANK" && hasBaseYearData) {
          // Handle BLANK cells for emission factors when dataPeriod is BASE_YEAR
          // Extract GHG type from columnCode (e.g., "GHGEMF-C2-CO2" -> "CO2")
          // Pattern: GHGEMF-{ColumnNumber}-{GHGType}
          const ghgMatch = element.columnCode ? element.columnCode.match(/-([A-Z]\d+)-([A-Z0-9]+)$/) : null;
          const ghgType = ghgMatch ? ghgMatch[2] : null;

          // Extract row number from columnNumber (e.g., "C2" -> 2, "C3" -> 3, "C4" -> 4)
          const rowNum = parseInt(element.columnNumber.replace(/[A-Z]/, ''));

          // Find the keyValue for this row by matching the row number
          // KeyValues have metadata.columnNumber like "B2", "B3", "B4" which correspond to rows
          const rowKeyValue = templatedata.keyValues.find(kv => {
            if (kv.metadata && kv.metadata.columnNumber) {
              const kvRowNum = parseInt(kv.metadata.columnNumber.replace(/[A-Z]/, ''));
              return kvRowNum === rowNum && kv.dataPeriod === 'BASE_YEAR' && kv.emissionFactors && kv.emissionFactors.length > 0;
            }
            return false;
          });

          if (rowKeyValue && ghgType && rowKeyValue.emissionFactors) {
            // Find the emission factor matching this GHG type
            const emissionFactor = rowKeyValue.emissionFactors.find(ef => ef.ghg === ghgType);
            if (emissionFactor && emissionFactor.factor !== undefined && emissionFactor.factor !== null) {
              const factorValue = Utils.roundToDecimalPlacesIfNumber(emissionFactor.factor, tableDp);
              contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right; height: 20px;font-size:14px"><span style="color: rgb(0, 0, 0);">${factorValue}</span></td>`
            } else {
              contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right; height: 20px;font-size:14px"><span style="color: rgb(0, 0, 0);">-</span></td>`
            }
          } else {
            contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right; height: 20px;font-size:14px"><span style="color: rgb(0, 0, 0);">-</span></td>`
          }
        } else {
          if (!!findLangV) {
            contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px"><span style="color: rgb(0, 0, 0);">${findLangV.text}</span></td>`
          } else {
            contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px"><span style="color: rgb(0, 0, 0);">-</span></td>`
          }
        }
      } else {
        contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px"><span style="color: rgb(0, 0, 0);">-</span></td>`
      }
    });

    contentHtml += `</tr>`
  }
  contentHtml += `</tbody></table></div>`
  return wrapTemplateBlock(contentHtml)
}

const pagetableTYPE_3Function = (templatedata, lang, settings) => {
  const tableDp = resolveUaeFdlTableDecimalPlaces(settings);
  var contentHtml = ''
  let findLangValue = templatedata.localization.find(val => matchesLanguageCode(val.languageCode, lang))
  if (!findLangValue) {
    findLangValue = templatedata.localization.find(val => matchesLanguageCode(val.languageCode, 'en'))
  }
  if (!!findLangValue) {
    contentHtml += `<div style="margin-bottom: 10px;font-size:14px;margin-left: 0;"><span style="color: rgb(52, 73, 94);"><strong>${findLangValue.text}</strong></span></div>`
  }
  contentHtml += `<div style="margin:0;padding:0;border: 1px solid #ddd;     border-radius: 6px;     overflow: hidden;">
  <table style='width:100%;font-size:14px;border-collapse: collapse; border-spacing: 0; border: 1px solid #fff; border-radius: 6px; overflow: hidden;'><tbody>`

  // Get all columns and sort them by columnOrder
  const allColumns = templatedata.metadata.sort((a, b) => a.columnOrder - b.columnOrder);

  // Group columns by row number (A1, A2, B1, B2 -> group by 1, 2, etc.)
  const rowGroups = {};
  allColumns.forEach(column => {
    const rowNum = column.columnNumber.replace(/[A-Z]/, ''); // Extract number (1, 2, 3, etc.)
    if (!rowGroups[rowNum]) rowGroups[rowNum] = {};
    rowGroups[rowNum][column.columnNumber] = column;
  });

  // Create rows - one row per row number, sorted by row number
  Object.keys(rowGroups).sort((a, b) => parseInt(a) - parseInt(b)).forEach(rowNum => {
    // Check if this row contains any HEADER type columns
    const hasHeader = Object.values(rowGroups[rowNum]).some(column => column.columnType === "HEADER");
    const rowStyle = hasHeader ? 'height: 20px; background-color: rgb(241, 247, 249);' : 'height: 20px;';
    contentHtml += `<tr style="${rowStyle}">`

    // Get all columns for this row, sorted by column letter (A, B, C, D, etc.)
    const rowColumns = Object.keys(rowGroups[rowNum]).sort((a, b) => a.charCodeAt(0) - b.charCodeAt(0));

    rowColumns.forEach(columnNumber => {
      const column = rowGroups[rowNum][columnNumber];
      let findLangV = column?.localization.find(val => matchesLanguageCode(val.languageCode, lang));
      if (!findLangV) {
        findLangV = column?.localization.find(val => matchesLanguageCode(val.languageCode, 'en'));
      }

      if (column.columnType === "STATIC_TEXT") {
        if (!!findLangV) {
          contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px">${findLangV.text}</td>`
        } else {
          contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px">-</td>`
        }
      } else if (column.columnType === "HEADER") {
        if (!!findLangV) {
          contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px;font-weight:800">${findLangV.text}</td>`
        } else {
          contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px;font-weight:800">-</td>`
        }
      } else if (column.columnType === "CODE_MAPPING") {
        const findAns = templatedata.keyValues.filter(item => {
          // Handle nested structure - check if item has sub-objects with metadata
          let matches = false;

          // Check if item has direct metadata
          if (item.metadata && item.metadata.columnCode === column.columnCode) {
            matches = true;
          }

          // Check if item has nested objects with metadata (like your data structure)
          if (!matches && item[0] && item[0].metadata && item[0].metadata.columnCode === column.columnCode) {
            matches = true;
          }

          return matches;
        });

        if (!!findAns && findAns.length > 0) {
          // For CODE_MAPPING, we should only show the first matching value in this cell
          const element1 = findAns[0]; // Take only the first match for this cell
          let dataForDisplay = element1;
          if (element1[0] && element1[0].value !== undefined) {
            // Use nested data if available
            dataForDisplay = element1[0];
          }

          const mappedText = formatGhgTableNumericAnswer(dataForDisplay, tableDp);
          contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px" class='${element1.key}'><span style="color: rgb(0, 0, 0);">${mappedText}</span></td>`
        } else {
          // Add empty cell if no mapping found
          contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px"><span style="color: rgb(0, 0, 0);">-</span></td>`
        }
      } else {
        if (!!findLangV) {
          contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px"><span style="color: rgb(0, 0, 0);">${findLangV.text}</span></td>`
        } else {
          contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left; height: 20px;font-size:14px"><span style="color: rgb(0, 0, 0);">-</span></td>`
        }
      }
    });

    contentHtml += `</tr>`
  });

  contentHtml += `</tbody></table></div>`
  return wrapTemplateBlock(contentHtml)
}

function pagetableTYPE_5Function(templatedata, lang, settings) {
  let contentHtml = `<div id="typeTableHolder-${templatedata.key}">`;
  let findLangValue = templatedata.localization.find(val => matchesLanguageCode(val.languageCode, lang));
  if (!findLangValue) {
    findLangValue = templatedata.localization.find(val => matchesLanguageCode(val.languageCode, 'en'));
  }
  if (!!findLangValue) {
    contentHtml += `<div style="margin-bottom: 10px;font-size:14px;margin-left: 0;"><span style=\"color: rgb(52, 73, 94);\"><strong>${findLangValue.text}</strong></span></div>`;
  }

  // Years from baseYear..currentYear inclusive (descending)
  const baseYear = templatedata.baseYear;
  const currentYear = templatedata.currentYear;
  const years = (typeof baseYear === 'number' && typeof currentYear === 'number' && currentYear >= baseYear)
    ? Array.from({ length: currentYear - baseYear + 1 }, (_, i) => currentYear - i)
    : [];
  const type5Dp = resolveUaeFdlTableDecimalPlaces(settings);

  contentHtml += `<div style="margin:0;padding:0;border: 1px solid #ddd; border-radius: 6px; overflow: hidden;">
  <table style='width:100%;font-size:14px;border-collapse: collapse; border-spacing: 0; border: 1px solid #fff; border-radius: 6px; overflow: hidden;'><tbody>`;

  // Header
  const headerColumns = templatedata.metadata
    .filter(item => /^[A-Z]1$/.test(item.columnNumber) && item.columnType === "HEADER")
    .sort((a, b) => a.columnOrder - b.columnOrder);
  contentHtml += `<tr style="background-color: rgb(241, 247, 249); height: 20px;">`;
  headerColumns.forEach(item => {
    let l = item.localization?.find(val => matchesLanguageCode(val.languageCode, lang));
    if (!l) {
      l = item.localization?.find(val => matchesLanguageCode(val.languageCode, 'en'));
    }
    const headerText = l ? l.text : '';
    contentHtml += `<th style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;font-weight:800;font-size:14px;\"><span style=\"color: rgb(0, 0, 0);\">${headerText}</span></th>`;
  });
  contentHtml += `<th style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;font-weight:800;font-size:14px;\"><span style=\"color: rgb(0, 0, 0);\">UOM</span></th>`;
  years.forEach(y => {
    contentHtml += `<th style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right;font-weight:800;font-size:14px;\"><span style=\"color: rgb(0, 0, 0);\">${y}</span></th>`;
  });
  contentHtml += `</tr>`;

  const codeMappingColumns = templatedata.metadata.filter(item => item.columnType === "CODE_MAPPING");

  // Render per metric as a section
  codeMappingColumns.forEach(metricColumn => {
    const metricData = templatedata.keyValues.find(item => item.metadata.columnCode === metricColumn.columnCode);
    if (!metricData) return;

    if (!metricData.metricId) return;

    // Row with metric name
    contentHtml += `<tr style=\"height: 20px; background-color: rgba(44, 192, 106, 0.08);\">`;
    const colNumber = Utils.getColumnNumber(metricColumn.columnNumber);
    for (let i = 1; i <= headerColumns.length; i++) {
      if (i === colNumber) {
        contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;font-size:14px;font-weight:600;\">${metricData.metricName || ''}</td>`;
      } else {
        contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204);\"></td>`;
      }
    }
    // UOM data
    contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204);\"></td>`;
    // Year cells
    years.forEach(() => {
      contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204);\"></td>`;
    });
    contentHtml += `</tr>`;

    // Build unique branches seen anywhere under this metric; order by businessUnits if provided
    const buOrder = (templatedata.businessUnits || []).map(b => ({ id: b.id, name: b.name }));
    const branchMap = new Map();
    (metricData.periodData || []).forEach(p => (p.branchData || []).forEach(b => branchMap.set(b.branchId || b.branchName, b.branchName)));
    (metricData.datapoints || []).sort((a, b) => a.sortOrder - b.sortOrder).forEach(dp => (dp.periodData || []).forEach(p => (p.branchData || []).forEach(b => branchMap.set(b.branchId || b.branchName, b.branchName))));
    let branches = Array.from(branchMap.entries()).map(([key, name]) => ({ key, name }));
    if (buOrder.length > 0) {
      const indexOf = (br) => {
        const i = buOrder.findIndex(b => b.id === br.key || b.name === br.name);
        return i === -1 ? Number.MAX_SAFE_INTEGER : i;
      }
      branches.sort((a, b) => indexOf(a) - indexOf(b));
    } else {
      branches.sort((a, b) => a.name.localeCompare(b.name));
    }

    branches.forEach(br => {
      const branchName = br.name;
      // Branch aggregate row (from metric totals branchData)
      contentHtml += `<tr style=\"height: 20px;\">`;
      for (let i = 1; i <= headerColumns.length; i++) {
        if (i === colNumber) {
          contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;font-size:14px;font-weight:600;\">${branchName}</td>`;
        } else {
          contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204);\"></td>`;
        }
      }
      // Empty cell for UOM
      contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204);padding: 10px; text-align: left;font-size:14px;font-weight:600;\">${metricData.unitOfMeasure}</td>`;
      years.forEach(y => {
        const p = (metricData.periodData || []).find(pp => new Date(pp.period).getFullYear() === y);
        const v = p ? (p.branchData || []).find(b => (b.branchId === br.key) || (b.branchName === branchName)) : null;
        let metricValue = v?.value ?? ''
        if (metricData.emissionFactorLinked) {
          metricValue = v?.emissionValue ? (v?.emissionValue / 1000) : '';
        } else if (metricData.emission) {
          metricValue = v?.solution ? (v?.solution / 1000) : '';
        }
        contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right;font-size:14px;\">${Utils.roundToDecimalPlacesIfNumber(metricValue, type5Dp)}</td>`;
      });
      contentHtml += `</tr>`;

      // Datapoints under this branch
      (metricData.datapoints || []).forEach(dp => {
        contentHtml += `<tr style=\"height: 20px;\">`;
        for (let i = 1; i <= headerColumns.length; i++) {
          if (i === colNumber) {
            contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;font-size:14px;\">${dp.datapointName}</td>`;
          } else {
            contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204);\"></td>`;
          }
        }
        // UOM data
        contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;font-size:14px;\">${dp.unitOfMeasure}</td>`;
        //Year data
        years.forEach(y => {
          const p = (dp.periodData || []).find(pp => new Date(pp.period).getFullYear() === y);
          const v = p ? (p.branchData || []).find(b => (b.branchId === br.key) || (b.branchName === branchName)) : null;
          let dpValueToUse = null;
          if (dp.emission) {
            dpValueToUse = v?.emissionValue ? (v?.emissionValue / 1000) : '';
          } else {
            dpValueToUse = v?.value ?? '';
          }
          contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right;font-size:14px;\">${Utils.roundToDecimalPlacesIfNumber(dpValueToUse, type5Dp)}</td>`;
        });
        contentHtml += `</tr>`;
      });
    });
  });
  contentHtml += `</tbody></table></div></div>`;
  return wrapTemplateBlock(contentHtml);
}

/**
 * 
 * Table data with base year to current year
 * 
 * @param {Object} templatedata - The template data object
 * @param {string} lang - The language code
 * @param {Object} settings - The settings object
 * @returns {string} - The HTML string for the table
 */

function pagetableTYPE_6Function(templatedata, lang, settings) {
  let contentHtml = `<div id="typeTableHolder-${templatedata.key}">`;
  let findLangValue = templatedata.localization.find(val => matchesLanguageCode(val.languageCode, lang));
  if (!findLangValue) {
    findLangValue = templatedata.localization.find(val => matchesLanguageCode(val.languageCode, 'en'));
  }
  if (!!findLangValue) {
    contentHtml += `<div style="margin-bottom: 10px;font-size:14px;margin-left: 0;"><span style=\"color: rgb(52, 73, 94);\"><strong>${findLangValue.text}</strong></span></div>`;
  }

  // Years from baseYear..currentYear inclusive (ascending order)
  const baseYear = templatedata.baseYear;
  const currentYear = templatedata.currentYear;
  const years = (typeof baseYear === 'number' && typeof currentYear === 'number' && currentYear >= baseYear)
    ? Array.from({ length: currentYear - baseYear + 1 }, (_, i) => baseYear + i)
    : (typeof currentYear === 'number' ? [currentYear] : []); // Fallback to currentYear only if baseYear missing
  const type6FractionDigits = resolveUaeFdlTableDecimalPlaces(settings);

  contentHtml += `<div style="margin:0;padding:0;border: 1px solid #ddd; border-radius: 6px; overflow: hidden;">
  <table style='width:100%;font-size:14px;border-collapse: collapse; border-spacing: 0; border: 1px solid #fff; border-radius: 6px; overflow: hidden;'><tbody>`;

  // Header
  const headerColumns = templatedata.metadata
    .filter(item => /^[A-Z]1$/.test(item.columnNumber) && item.columnType === "HEADER")
    .sort((a, b) => a.columnOrder - b.columnOrder);
  contentHtml += `<tr style="background-color: rgb(241, 247, 249); height: 20px;">`;
  headerColumns.forEach(item => {
    let l = item.localization?.find(val => matchesLanguageCode(val.languageCode, lang));
    if (!l) {
      l = item.localization?.find(val => matchesLanguageCode(val.languageCode, 'en'));
    }
    const headerText = l ? l.text : '';
    contentHtml += `<th style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;font-weight:800;font-size:14px;\"><span style=\"color: rgb(0, 0, 0);\">${headerText}</span></th>`;
  });

  // Base Year column header
  if (typeof baseYear === 'number') {
    contentHtml += `<th style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right;font-weight:800;font-size:14px;\"><span style=\"color: rgb(0, 0, 0);\">Base Year (${baseYear})</span></th>`;
  }

  // Year columns (from baseYear+1 to currentYear)
  years.filter(y => y !== baseYear).forEach(y => {
    contentHtml += `<th style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right;font-weight:800;font-size:14px;\"><span style=\"color: rgb(0, 0, 0);\">${y}</span></th>`;
  });

  // % Change column header
  if (typeof baseYear === 'number' && typeof currentYear === 'number' && currentYear > baseYear) {
    contentHtml += `<th style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right;font-weight:800;font-size:14px;\"><span style=\"color: rgb(0, 0, 0);\">% Change (${baseYear} → ${currentYear})</span></th>`;
  }

  contentHtml += `</tr>`;

  // Get all metrics (keyValues with CODE_MAPPING type and metricId)
  // Deduplicate by metricId to avoid showing the same metric multiple times
  const metricsMap = new Map();
  templatedata.keyValues.forEach(item => {
    if (item.metadata &&
      item.metadata.columnType === "CODE_MAPPING" &&
      item.metricId) {
      // If metricId already exists, keep the one with lower sortOrder or first occurrence
      if (!metricsMap.has(item.metricId) ||
        (item.sortOrder || 0) < (metricsMap.get(item.metricId).sortOrder || 0)) {
        metricsMap.set(item.metricId, item);
      }
    }
  });
  const metrics = Array.from(metricsMap.values())
    .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)); // Sort by sortOrder if available

  // Store values for Total row calculation
  const totalValuesByYear = {};
  const allYears = baseYear !== undefined ? [baseYear, ...years.filter(y => y !== baseYear)] : years;

  // Render per metric - company-level only (no branches, no datapoints)
  metrics.forEach(metricData => {
    // Get the column number from metadata
    const metricColumn = templatedata.metadata.find(item => item.columnCode === metricData.metadata.columnCode);
    const colNumber = metricColumn ? Utils.getColumnNumber(metricColumn.columnNumber) : 1;

    // Single row per metric with company-level values
    contentHtml += `<tr style=\"height: 20px;\">`;
    for (let i = 1; i <= headerColumns.length; i++) {
      if (i === colNumber) {
        contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;font-size:14px;font-weight:600;\">${metricData.metricName || ''}</td>`;
      } else {
        contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204);\"></td>`;
      }
    }

    // Collect values for this metric across all years
    const metricValuesByYear = {};

    // Base Year column
    if (typeof baseYear === 'number') {
      const periodData = (metricData.periodData || []).find(p => new Date(p.period).getFullYear() === baseYear);
      let metricValue = '';

      if (periodData) {
        if (metricData.emissionFactorLinked && periodData.emissionValue !== undefined && periodData.emissionValue !== null && periodData.emissionValue !== 0) {
          metricValue = periodData.emissionValue / 1000;
        } else if (metricData.emission && periodData.emissionValue !== undefined && periodData.emissionValue !== null && periodData.emissionValue !== 0) {
          metricValue = periodData.emissionValue / 1000;
        } else if (periodData.totalValue !== undefined && periodData.totalValue !== null) {
          metricValue = periodData.totalValue;
        } else if (periodData.value !== undefined && periodData.value !== null) {
          metricValue = periodData.value;
        }
      }

      metricValuesByYear[baseYear] = parseFloat(metricValue) || 0;
      totalValuesByYear[baseYear] = (totalValuesByYear[baseYear] || 0) + metricValuesByYear[baseYear];
      const displayBase = (metricValue !== '' && metricValue !== null && metricValue !== undefined) ? Utils.formatNumberWithThousandSeparators(metricValue, type6FractionDigits) : '';
      contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right;font-size:14px;\">${displayBase}</td>`;
    }

    // Year columns (from baseYear+1 to currentYear)
    years.filter(y => y !== baseYear).forEach(y => {
      const periodData = (metricData.periodData || []).find(p => new Date(p.period).getFullYear() === y);
      let metricValue = '';

      if (periodData) {
        if (metricData.emissionFactorLinked && periodData.emissionValue !== undefined && periodData.emissionValue !== null && periodData.emissionValue !== 0) {
          metricValue = periodData.emissionValue / 1000;
        } else if (metricData.emission && periodData.emissionValue !== undefined && periodData.emissionValue !== null && periodData.emissionValue !== 0) {
          metricValue = periodData.emissionValue / 1000;
        } else if (periodData.totalValue !== undefined && periodData.totalValue !== null) {
          metricValue = periodData.totalValue;
        } else if (periodData.value !== undefined && periodData.value !== null) {
          metricValue = periodData.value;
        }
      }

      metricValuesByYear[y] = parseFloat(metricValue) || 0;
      totalValuesByYear[y] = (totalValuesByYear[y] || 0) + metricValuesByYear[y];
      const displayYear = (metricValue !== '' && metricValue !== null && metricValue !== undefined) ? Utils.formatNumberWithThousandSeparators(metricValue, type6FractionDigits) : '';
      contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right;font-size:14px;\">${displayYear}</td>`;
    });

    // % Change column
    if (typeof baseYear === 'number' && typeof currentYear === 'number' && currentYear > baseYear) {
      const baseValue = metricValuesByYear[baseYear] || 0;
      const currentValue = metricValuesByYear[currentYear] || 0;
      let percentChange = '';

      if (baseValue !== 0) {
        const change = ((currentValue - baseValue) / baseValue) * 100;
        percentChange = Utils.formatNumberWithThousandSeparators(change, type6FractionDigits);
      } else if (currentValue !== 0) {
        percentChange = 'N/A'; // Can't calculate % change when base is 0
      } else {
        percentChange = '0';
      }

      contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right;font-size:14px;\">${percentChange}</td>`;
    }

    contentHtml += `</tr>`;
  });

  // Total row
  if (metrics.length > 0) {
    contentHtml += `<tr style=\"height: 20px; background-color: rgba(44, 192, 106, 0.08);\">`;
    const firstHeaderCol = headerColumns[0];
    const colNumber = firstHeaderCol ? Utils.getColumnNumber(firstHeaderCol.columnNumber) : 1;

    for (let i = 1; i <= headerColumns.length; i++) {
      if (i === colNumber) {
        contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;font-size:14px;font-weight:600;\">Total</td>`;
      } else {
        contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204);\"></td>`;
      }
    }

    // Base Year total
    if (typeof baseYear === 'number') {
      const totalValue = totalValuesByYear[baseYear] || 0;
      contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right;font-size:14px;font-weight:600;\">${Utils.formatNumberWithThousandSeparators(totalValue, type6FractionDigits)}</td>`;
    }

    // Year totals (from baseYear+1 to currentYear)
    years.filter(y => y !== baseYear).forEach(y => {
      const totalValue = totalValuesByYear[y] || 0;
      contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right;font-size:14px;font-weight:600;\">${Utils.formatNumberWithThousandSeparators(totalValue, type6FractionDigits)}</td>`;
    });

    // Total % Change
    if (typeof baseYear === 'number' && typeof currentYear === 'number' && currentYear > baseYear) {
      const baseTotal = totalValuesByYear[baseYear] || 0;
      const currentTotal = totalValuesByYear[currentYear] || 0;
      let percentChange = '';

      if (baseTotal !== 0) {
        const change = ((currentTotal - baseTotal) / baseTotal) * 100;
        percentChange = Utils.formatNumberWithThousandSeparators(change, type6FractionDigits);
      } else if (currentTotal !== 0) {
        percentChange = 'N/A';
      } else {
        percentChange = '0';
      }

      contentHtml += `<td style=\"border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: right;font-size:14px;font-weight:600;\">${percentChange}</td>`;
    }

    contentHtml += `</tr>`;
  }

  contentHtml += `</tbody></table></div></div>`;
  return wrapTemplateBlock(contentHtml);
}


/**
 * Renders TYPE_7_TABLE (Table Qualitative): same structure as Type 5 (metric, entity, rows)
 * but with only one data column: after each question the answer shows.
 * API shape: element.keyValues[] with metadata.columnType === 'CODE_MAPPING' each have
 * branchQualitativeData: [{ branchId, branchName, questionAnswers }].
 */
function pagetableTYPE_7Function(templatedata, lang, settings) {
  const escapeHtml = (s) => {
    if (s == null || s === '') return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  };
  const escapeHtmlText = (s) => (s == null || s === '' ? '-' : escapeHtml(s));
  const nl2br = (s) => (s == null ? '' : String(s).replace(/\n/g, '<br/>'));

  let contentHtml = `<div id="typeTableHolder-${templatedata.key}">`;
  let findLangValue = templatedata.localization?.find(val => matchesLanguageCode(val.languageCode, lang));
  if (!findLangValue && templatedata.localization?.length) {
    findLangValue = templatedata.localization.find(val => matchesLanguageCode(val.languageCode, 'en'));
  }
  if (!!findLangValue && findLangValue.text) {
    contentHtml += `<div style="margin-bottom: 10px;font-size:14px;margin-left: 0;"><span style="color: rgb(52, 73, 94);"><strong>${escapeHtmlText(findLangValue.text)}</strong></span></div>`;
  }

  const getAnswerDisplay = (qa) => {
    if (qa.combinedAnswer != null && qa.combinedAnswer !== '') {
      return nl2br(escapeHtml(qa.combinedAnswer));
    }
    const byMonth = qa.answersByMonth;
    if (byMonth && typeof byMonth === 'object' && Object.keys(byMonth).length > 0) {
      const parts = Object.entries(byMonth)
        .map(([month, text]) => (text != null && text !== '' ? `${month}: ${text}` : null))
        .filter(Boolean);
      return parts.length > 0 ? nl2br(escapeHtml(parts.join('\n'))) : '-';
    }
    return '-';
  };

  const sortByOrder = (arr) => {
    if (!arr || !Array.isArray(arr)) return [];
    return [...arr].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  };

  const headerColumns = (templatedata.metadata || [])
    .filter((item) => /^[A-Z]1$/.test(item.columnNumber) && item.columnType === 'HEADER')
    .sort((a, b) => (a.columnOrder || 0) - (b.columnOrder || 0));
  const codeMappingColumns = (templatedata.metadata || []).filter((item) => item.columnType === 'CODE_MAPPING');
  const buOrder = (templatedata.businessUnits || []).map((b) => ({ id: b.id, name: b.name }));

  const orderBranches = (branchList) => {
    if (!branchList || branchList.length === 0) return [];
    if (buOrder.length > 0) {
      const indexOf = (br) => {
        const i = buOrder.findIndex((b) => b.id === br.branchId || b.name === br.branchName);
        return i === -1 ? Number.MAX_SAFE_INTEGER : i;
      };
      return [...branchList].sort((a, b) => indexOf(a) - indexOf(b));
    }
    return [...branchList].sort((a, b) => (a.branchName || '').localeCompare(b.branchName || ''));
  };

  const headerText =
    headerColumns.length > 0
      ? (() => {
        const item = headerColumns[0];
        let l = item.localization?.find((val) => matchesLanguageCode(val.languageCode, lang));
        if (!l && item.localization?.length) l = item.localization.find((val) => matchesLanguageCode(val.languageCode, 'en'));
        return l ? l.text : 'Questions';
      })()
      : 'Questions';

  contentHtml += `<div style="margin:0;padding:0;border: 1px solid #ddd; border-radius: 6px; overflow: hidden;">
  <table style="width:100%;font-size:14px;border-collapse: collapse; border-spacing: 0; border: 1px solid #fff; border-radius: 6px; overflow: hidden;"><tbody>`;
  contentHtml += `<tr style="background-color: rgb(241, 247, 249); height: 20px;">`;
  contentHtml += `<th style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;font-weight:800;font-size:14px;"><span style="color: rgb(0, 0, 0);">${escapeHtml(headerText)}</span></th>`;
  contentHtml += `</tr>`;

  codeMappingColumns.forEach((metricColumn) => {
    const metricData = (templatedata.keyValues || []).find((item) => item.metadata && item.metadata.columnCode === metricColumn.columnCode);
    if (!metricData) return;

    const metricName = metricData.metricName || metricColumn.columnCode || '';

    contentHtml += `<tr style="height: 20px; background-color: rgba(44, 192, 106, 0.08);">`;
    contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;font-size:14px;font-weight:600;">${escapeHtml(metricName)}</td>`;
    contentHtml += `</tr>`;

    const branchData = metricData.branchQualitativeData || [];
    const orderedBranches = orderBranches(branchData);
    let metricHasAnyShownBranch = false;

    orderedBranches.forEach((br) => {
      const questionAnswers = br.questionAnswers || [];
      const hasAnyChildQuestions = questionAnswers.some((qa) => (qa.children || []).length > 0);
      if (!hasAnyChildQuestions) return;

      metricHasAnyShownBranch = true;
      const branchName = br.branchName != null ? br.branchName : (br.branchId != null ? String(br.branchId) : '-');
      contentHtml += `<tr style="height: 20px;">`;
      contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;font-size:14px;font-weight:600;">${escapeHtml(branchName)}</td>`;
      contentHtml += `</tr>`;

      const questionPadding = 10;
      const answerPadding = 10;
      questionAnswers.forEach((qa) => {
        const children = (qa.children || []);
        sortByOrder(children).forEach((child) => {
          const questionText = child.questionText != null ? nl2br(escapeHtml(child.questionText)) : '-';
          const answerHtml = getAnswerDisplay(child);
          const answerDisplay = answerHtml && answerHtml.trim() !== '' ? answerHtml : '-';
          contentHtml += `<tr style="height: 20px;">`;
          contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px 10px 10px ${questionPadding}px; text-align: left;font-size:14px;"><strong><em>${questionText}</em></strong></td>`;
          contentHtml += `</tr>`;
          contentHtml += `<tr style="height: 20px;">`;
          contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px 10px 10px ${answerPadding}px; text-align: left;font-size:14px;"><em>${answerDisplay}</em></td>`;
          contentHtml += `</tr>`;
        });
      });
    });

    if (!metricHasAnyShownBranch) {
      contentHtml += `<tr style="height: 20px;">`;
      contentHtml += `<td style="border: 1px solid rgb(204, 204, 204); padding: 10px; text-align: left;font-size:14px;">-</td>`;
      contentHtml += `</tr>`;
    }
  });

  contentHtml += `</tbody></table></div></div>`;
  return wrapTemplateBlock(contentHtml);
}

const buildUaeFdlTemplatePageHtml = (templateData, lang, type, settings) => {
  if (!!templateData && templateData.length > 0) {
    var htmlContent = ''
    for (let index = 0; index < templateData.length; index++) {
      const element = templateData[index];
      if (element.keyValueType == "PAGE") {
        htmlContent += pageHeaderFunction(element, lang, type)
      }
      if (element.keyValueType == "SECTION") {
        htmlContent += pageSectionFunction(element, lang)
      }
      if (element.keyValueType == "SUB_SECTION") {
        htmlContent += pageSubSectionFunction(element, lang, type)
      }
      if (element.keyValueType == "SINGLE_VALUE") {
        if (!!element.keyValues && element.keyValues.length > 0 && !!qAnserData(element.keyValues[0])) {
          htmlContent += pageQuestionFunction(element, lang)
        }
      }
      if (element.keyValueType == "TYPE_1_TABLE") {
        htmlContent += pagetableTYPE_1Function(element, lang)
      }
      if (element.keyValueType == "TYPE_2_TABLE") {
        htmlContent += pagetableTYPE_2Function(element, lang, settings)
      }
      if (element.keyValueType == "TYPE_3_TABLE") {
        htmlContent += pagetableTYPE_3Function(element, lang, settings)
      }
      if (element.keyValueType == "TYPE_5_TABLE") {
        htmlContent += pagetableTYPE_5Function(element, lang, settings)
      }
      if (element.keyValueType == "TYPE_6_TABLE") {
        htmlContent += pagetableTYPE_6Function(element, lang, settings)
      }
      if (element.keyValueType == "TYPE_7_TABLE") {
        htmlContent += pagetableTYPE_7Function(element, lang, settings)
      }
    }
    htmlContent += `</div>`
    return `<div style="font-family: ${UAE_FDL_REPORT_FONT_FAMILY}; line-height: 1.6; color: #333;">${htmlContent}</div>`
  } else {
    return `<div style="font-family: ${UAE_FDL_REPORT_FONT_FAMILY};"></div>`
  }

}

const buildSectionBadgeHtml = (sectionLabel, badgeColor) => `
  <div style="display:none;margin:0;padding:0;">
    <div style="display: inline-block;background-color: ${badgeColor};padding: 5px 5px 5px 0;margin: 0;border-radius: 0px 50px 50px 0px;max-width: fit-content;">
      <h3 style="color: #fff;margin: 0;margin-left: 0;padding: 0;white-space: nowrap;">${sectionLabel}</h3>
    </div>
  </div>
  <div style="margin:0 0 4px 0;padding:0;">
    <div style="display: inline-block;background-color: ${badgeColor};padding: 5px 5px 5px 0;margin: 0;border-radius: 0px 50px 50px 0px;">
      <h3 style="color: #fff;margin: 0;margin-left: 0;padding: 0;white-space: nowrap;">${sectionLabel}</h3>
    </div>
  </div>`;

const buildPageIntroHtml = ({
  sectionLabel,
  pageLabel,
  contentLabel = '',
  badgeColor,
  templateHtml = '',
  extraContentHtml = '',
}) => {
  const descriptionHtml = contentLabel
    ? `<p style="text-align: justify; margin: 0 0 15px 0;">${contentLabel}</p>`
    : '';
  const pageTitleHtml = sectionLabel === pageLabel
    ? ''
    : `<h2 style="font-size: 18px; font-weight: bold; margin: 0 0 15px 0;">${pageLabel}</h2>`;
  const templateSectionHtml = templateHtml
    ? `<div style="margin-top: 20px;">${templateHtml}</div>`
    : '';

  return `<div style="font-family: ${UAE_FDL_REPORT_FONT_FAMILY}; padding: 0; margin: 0; background-color: #fff; color: #333; line-height: 1.6;">
    ${buildSectionBadgeHtml(sectionLabel, badgeColor)}
    <div style="padding:0;margin:16px 0 0 0;">
      ${pageTitleHtml}
      ${descriptionHtml}
      ${extraContentHtml}
    </div>
    ${templateSectionHtml}
  </div>`;
};

const buildTemplateHtml = (pageData, language, sectionLabel, settings) => {
  const normalizedLang = normalizeLanguageCode(language);
  if (!hasTemplateData(pageData)) return '';
  return UAEFDLReportingUtils.buildTemplatePageHtml(pageData, normalizedLang, sectionLabel, settings);
};

const buildObjectivesListHtml = (language, i18n) => {
  const objectives = [1, 2, 3, 4, 5].map((n) =>
    `<li style="margin-bottom: 8px;">${getTranslation(i18n, `UAEFDLReport.DecreeLawObjective${n}.label`, language)}</li>`,
  ).join('');
  return `<ol style="padding-left: 20px; margin-bottom: 15px;">${objectives}</ol>`;
};

const buildPurposeBulletListHtml = (language, i18n) => {
  const bullets = [1, 2, 3, 4, 5].map((n) =>
    `<li style="margin-bottom: 8px;">${getTranslation(i18n, `UAEFDLReport.PurposeBullet${n}.label`, language)}</li>`,
  ).join('');
  return `<ul style="padding-left: 20px; margin-bottom: 15px;">${bullets}</ul>`;
};

const buildInventoryPurposeStaticHtml = (language, i18n) => {
  const purposeHeading = getTranslation(i18n, 'UAEFDLReport.PurposeOfGHGInventory.label', language);
  const purposeIntro = getTranslation(i18n, 'UAEFDLReport.PurposeOfGHGInventoryContent.label', language);
  return `
    <h2 style="font-size: 18px; font-weight: bold; margin-bottom: 10px; margin-top: 20px;">${purposeHeading}</h2>
    <p style="text-align: justify; margin-bottom: 15px;">${purposeIntro}</p>
    ${buildPurposeBulletListHtml(language, i18n)}`;
};

const buildSinglePageSection = ({
  sectionTitleKey,
  pageTitleKey,
  contentKey,
  badgeColor,
  sectionType,
  pageType,
  language,
  i18n,
  settings,
  pageData,
  extraContentHtml = '',
  sortOrder,
  index,
}) => {
  const sectionLabel = getTranslation(i18n, sectionTitleKey, language);
  const pageLabel = getTranslation(i18n, pageTitleKey, language);
  const contentLabel = contentKey ? getTranslation(i18n, contentKey, language) : '';
  const templateHtml = buildTemplateHtml(pageData || [], language, sectionLabel, settings);

  return {
    name: sectionLabel,
    index,
    sortOrder,
    type: sectionType,
    pages: [{
      name: pageLabel,
      index: 0,
      sortOrder: 1,
      type: pageType,
      html: buildPageIntroHtml({
        sectionLabel,
        pageLabel,
        contentLabel,
        badgeColor,
        templateHtml,
        extraContentHtml,
      }),
    }],
  };
};

export default class UAEFDLReportingUtils {
  static buildTemplatePageHtml(templateData, lang, sectionLabel, settings) {
    return buildUaeFdlTemplatePageHtml(templateData, lang, sectionLabel, settings);
  }

  static getCoverSection(companyId, year, t, reportData = {}, language, i18n) {
    const logoURL = API_ROUTES.PROFILESETTINGS.GETCOMPANYLOGO.replace("<companyId>", companyId);

    const version = reportData.version || "1";
    const generatedBy = reportData.generatedBy || reportData.companyName || "System";
    const currentDate = reportData.date || new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\//g, '-');

    const coverHtml = buildModernReportCoverHtml({
      logoUrl: logoURL,
      badgeLabel: getTranslation(i18n, "UAE_FDL.label", language),
      reportingStandardsLabel: getTranslation(i18n, "ReportingStandards.label", language),
      title: getTranslation(i18n, "UAEFDLReport.FullTitle.label", language),
      year,
      versionLabel: getTranslation(i18n, "Version.label", language),
      generatedByLabel: getTranslation(i18n, "GeneratedBy.label", language),
      dateLabel: getTranslation(i18n, "Date.label", language),
      version,
      generatedBy,
      currentDate,
    });

    return {
      name: getTranslation(i18n, "FrontCoverpage.label", language),
      index: 0,
      sortOrder: 1,
      type: ReportSection.TITLE,
      pages: [{
        name: getTranslation(i18n, "FrontCoverpage.label", language),
        sortOrder: 1,
        index: 0,
        type: ReportPageType.TITLE,
        html: coverHtml,
      }]
    };
  }

  static getTableOfContentsSection(t, language, i18n) {
    const tocHtml = buildUaeFdlTableOfContentsHtml((key) =>
      escapeTocHtml(getTranslation(i18n, key, language)),
    );

    return {
      name: getTranslation(i18n, "TableofContents.label", language),
      index: 1,
      sortOrder: 2,
      type: ReportSection.CONTENTS,
      pages: [{
        name: getTranslation(i18n, "TableofContents.label", language),
        sortOrder: 1,
        index: 0,
        type: ReportPageType.CONTENTS,
        html: tocHtml,
      }]
    };
  }

  /** Section: About the Organization | Page: About the Organization | pageData[0] */
  static getAboutTheOrganizationSection(pageData, language, t, i18n, settings, sortOrder = 3, index = 2) {
    return buildSinglePageSection({
      sectionTitleKey: 'UAEFDLReport.AboutTheOrganization.label',
      pageTitleKey: 'UAEFDLReport.AboutTheOrganization.label',
      contentKey: 'UAEFDLReport.AboutTheOrganizationContent.label',
      badgeColor: '#007bff',
      sectionType: ReportSection.UAE_FDL_ABOUT_THE_ORGANIZATION,
      pageType: ReportPageType.UAE_FDL_ABOUT_THE_ORGANIZATION,
      language,
      i18n,
      settings,
      pageData,
      sortOrder,
      index,
    });
  }

  /** Section: About the Report | Page: Objectives of the Decree-Law | pageData[1] */
  static getAboutTheReportSection(pageData, language, t, i18n, settings, sortOrder = 4, index = 3) {
    return buildSinglePageSection({
      sectionTitleKey: 'UAEFDLReport.AboutTheReport.label',
      pageTitleKey: 'UAEFDLReport.ObjectivesOfDecreeLaw.label',
      contentKey: 'UAEFDLReport.DecreeLawAimsAtContent.label',
      badgeColor: '#007bff',
      sectionType: ReportSection.UAE_FDL_ABOUT_THE_REPORT,
      pageType: ReportPageType.UAE_FDL_OBJECTIVES_OF_DECREE_LAW,
      language,
      i18n,
      settings,
      pageData,
      extraContentHtml: buildObjectivesListHtml(language, i18n),
      sortOrder,
      index,
    });
  }

  /** Section: Inventory of GHG Emissions | Page: GHG Emissions Inventory | pageData[2] */
  static getInventoryOfGHGEmissionsSection(pageData, language, t, i18n, settings, sortOrder = 5, index = 4) {
    return buildSinglePageSection({
      sectionTitleKey: 'UAEFDLReport.InventoryOfGHGEmissions.label',
      pageTitleKey: 'UAEFDLReport.GHGEmissionsInventory.label',
      contentKey: 'UAEFDLReport.GHGEmissionsInventoryContent.label',
      badgeColor: '#28a745',
      sectionType: ReportSection.UAE_FDL_INVENTORY_OF_GHG_EMISSIONS,
      pageType: ReportPageType.UAE_FDL_GHG_EMISSIONS_INVENTORY,
      language,
      i18n,
      settings,
      pageData,
      extraContentHtml: buildInventoryPurposeStaticHtml(language, i18n),
      sortOrder,
      index,
    });
  }

  /** Section: Information about emissions | Page: Information about emissions | pageData[3] */
  static getInformationAboutEmissionsSection(pageData, language, t, i18n, settings, sortOrder = 6, index = 5) {
    return buildSinglePageSection({
      sectionTitleKey: 'UAEFDLReport.InformationAboutEmissions.label',
      pageTitleKey: 'UAEFDLReport.InformationAboutEmissions.label',
      contentKey: '',
      badgeColor: '#28a745',
      sectionType: ReportSection.UAE_FDL_INFORMATION_ABOUT_EMISSIONS,
      pageType: ReportPageType.UAE_FDL_INFORMATION_ABOUT_EMISSIONS,
      language,
      i18n,
      settings,
      pageData,
      sortOrder,
      index,
    });
  }

  /** Section: Decarbonization targets and strategy | Page: Decarbonization Targets and Strategy | pageData[4] */
  static getDecarbonizationStrategySection(pageData, language, t, i18n, settings, sortOrder = 7, index = 6) {
    const sectionLabel = getTranslation(i18n, 'UAEFDLReport.DecarbonizationSection.label', language);
    const pageLabel = getTranslation(i18n, 'UAEFDLReport.DecarbonizationTargetsStrategy.label', language);
    const templateHtml = buildTemplateHtml(pageData, language, sectionLabel, settings);
    const contentLabel = getTranslation(i18n, 'UAEFDLReport.DecarbonizationTargetsStrategyContent.label', language);
    const extraContentHtml = `<p style="text-align: justify; margin-bottom: 15px;">${getTranslation(i18n, 'UAEFDLReport.DecarbonizationTargetsStrategyContent2.label', language)}</p>`;

    return {
      name: sectionLabel,
      index,
      sortOrder,
      type: ReportSection.UAE_FDL_DECARBONIZATION_TARGETS_STRATEGY,
      pages: [{
        name: pageLabel,
        index: 0,
        sortOrder: 1,
        type: ReportPageType.UAE_FDL_DECARBONIZATION_TARGETS_STRATEGY,
        html: buildPageIntroHtml({
          sectionLabel,
          pageLabel,
          contentLabel,
          badgeColor: '#fd7e14',
          templateHtml,
          extraContentHtml,
        }),
      }],
    };
  }

  /** Section: Verification & Compliance | Page: Verification & Compliance under UAE Federal Decree-Law | pageData[5] */
  static getVerificationComplianceSection(pageData, language, t, i18n, settings, sortOrder = 8, index = 7) {
    return buildSinglePageSection({
      sectionTitleKey: 'UAEFDLReport.VerificationSection.label',
      pageTitleKey: 'UAEFDLReport.VerificationCompliance.label',
      contentKey: 'UAEFDLReport.VerificationComplianceContent.label',
      badgeColor: '#6c757d',
      sectionType: ReportSection.UAE_FDL_VERIFICATION_COMPLIANCE,
      pageType: ReportPageType.UAE_FDL_VERIFICATION_COMPLIANCE,
      language,
      i18n,
      settings,
      pageData,
      sortOrder,
      index,
    });
  }

  /** Section: Insights | Page: Insights | pageData[6] */
  static getInsightsSection(pageData, language, t, i18n, settings, sortOrder = 9, index = 8) {
    return buildSinglePageSection({
      sectionTitleKey: 'UAEFDLReport.Insights.label',
      pageTitleKey: 'UAEFDLReport.Insights.label',
      contentKey: '',
      badgeColor: '#8e44ad',
      sectionType: ReportSection.UAE_FDL_INSIGHTS,
      pageType: ReportPageType.UAE_FDL_INSIGHTS,
      language,
      i18n,
      settings,
      pageData,
      sortOrder,
      index,
    });
  }
}
